import React, { useRef } from 'react'
import { Icon } from '~/components/icon'
import { UseRadio } from '../hooks/use-radio'
import { useRadioContext } from '../radio'
import {
  RadioButtonVariants,
  RadioIcoCheckedVariants,
  RadioIcoVariants,
  radioContainerVariants,
  radioVariants,
} from '../radio.styles'
import type { RadioScopedProps, TRadioProps } from '../radio.types'

export const RadioButton = (
  props: Omit<RadioScopedProps<TRadioProps>, 'size'>
) => {
  const {
    __scopeRadio,
    name,
    value,
    checked,
    children,
    onChange,
    iconOnly,
    id: radioId = null,
    ...rest
  } = props

  const {
    disabled,
    size = 'lg',
    id,
    readOnly,
    error,
    selectedValue,
  } = useRadioContext('RadioButton', __scopeRadio)

  const { iconSizeMapping } = UseRadio()
  const radioRef = useRef<HTMLInputElement>(null)
  const isDefaultValue = value === selectedValue
  const hasChecked = checked ? checked : isDefaultValue

  const hasLabel = () => {
    if (React.isValidElement(children)) {
      return !!children.props.children
    }
    return false
  }

  return (
    <div
      className={radioContainerVariants({
        disabled,
        readOnly,
        hasIconOnly: iconOnly || !hasLabel(),
        hasError: error,
      })}
    >
      <label
        className={radioVariants({
          disabled,
          readOnly,
          size,
          hasIconOnly: iconOnly || !hasLabel(),
          hasError: error,
        })}
        aria-disabled={disabled}
      >
        <input
          ref={radioRef}
          type="radio"
          className={RadioButtonVariants({ hasError: error })}
          id={radioId ? radioId : id}
          disabled={disabled}
          name={name}
          value={value}
          checked={hasChecked}
          onChange={onChange}
          defaultChecked={hasChecked}
          {...rest}
        />
        <span className={RadioIcoVariants({ size })}>
          <Icon
            size={iconSizeMapping[size]}
            symbol={'rdicon-radio-button-unselected'}
            color={
              disabled
                ? 'colorTextNeutralDisabled'
                : readOnly
                  ? 'colorTextNeutralReadonly'
                  : 'colorTextNeutralDefault'
            }
          />
        </span>
        <span className={RadioIcoCheckedVariants({ size })}>
          <Icon
            size={iconSizeMapping[size]}
            symbol={'rdicon-radio-button-selected'}
            color={
              disabled
                ? 'colorTextNeutralDisabled'
                : readOnly
                  ? 'colorTextNeutralReadonly'
                  : 'colorTextNeutralDefault'
            }
          />
        </span>
        {!iconOnly && children}
      </label>
    </div>
  )
}
